import { AccessTime, AttachMoney, BeachAccess, BusinessCenter, Event } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useGetEmployeeCurrentContractQuery } from '../../store/redux/reducers/contractSlice'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { formatStringDate } from '../../store/utils/dateUtils'
import LoadingWrapper from '../Loading/LoadingWrapper'
import DetailsTile from '../Tile/DetailsTile'

type EmploymentDescriptionProps = {
  isMyProfile: boolean
}

const EmploymentDescription = ({ isMyProfile }: EmploymentDescriptionProps) => {
  const isSidebarOpen = useSelector(getSidebarState)
  const { id: userPathId } = useParams()
  const currentUser = useSelector(getCurrentUser)

  const { data: contract, isLoading: isContractLoading } = useGetEmployeeCurrentContractQuery(
    {
      id: isMyProfile ? (currentUser?.id as string) : (userPathId as string),
    },
    { skip: isMyProfile ? !currentUser?.id : !userPathId }
  )

  const getContractDurationDescription = () => {
    if (!contract) {
      return '-'
    }

    const from = formatStringDate(contract.startDate)

    if (contract.indefiniteDuration || !contract.endDate) {
      return t('durationIndefinite', { from })
    }

    return t('fromDateToDate', {
      from,
      to: formatStringDate(contract.endDate),
    })
  }

  if (!isContractLoading && !contract) {
    return <Typography fontSize={16}>{t('page.usersProfile.tiles.employment.noEmployment')}</Typography>
  }

  return (
    <LoadingWrapper isLoading={isContractLoading}>
      <Stack gap={2} direction={{ xs: 'column', smMid: 'row', md: isSidebarOpen ? 'row' : 'column', lg: 'column' }}>
        <Stack gap={2} width={{ xs: '100%', smMid: '50%', md: '100%' }}>
          <DetailsTile
            title={t('page.usersProfile.tiles.employment.type')}
            description={contract ? contract.contractTypeValue : '-'}
            icon={<BusinessCenter />}
          />

          <DetailsTile
            title={t('page.usersProfile.tiles.employment.duration')}
            description={getContractDurationDescription()}
            icon={<Event />}
          />

          <DetailsTile
            title={t('page.usersProfile.tiles.employment.settlementType')}
            description={contract ? t(`page.employments.settlementsTypes.${contract.settlementMethod}`) : '-'}
            icon={<AttachMoney />}
          />

          <DetailsTile
            title={t('page.usersProfile.tiles.employment.percentage')}
            description={contract ? contract.employmentPercentage + ' %' : '-'}
            icon={<AccessTime />}
          />

          <DetailsTile
            title={t('page.employments.table.paidVacation')}
            description={
              contract
                ? contract.paidVacation
                  ? t('page.employments.table.yes')
                  : t('page.employments.table.no')
                : '-'
            }
            icon={<BeachAccess />}
          />

          {!contract?.paidVacation && (
            <DetailsTile
              title={t('page.usersProfile.tiles.employment.vacationPeriods')}
              description={
                contract?.paidVacationPeriods?.[0]
                  ? t(`fromDateToDate`, {
                      from: contract.paidVacationPeriods[0].startDate,
                      to: contract.paidVacationPeriods[0].endDate,
                    })
                  : '-'
              }
              icon={<BeachAccess />}
              info={true}
              tooltip={t('page.usersProfile.tiles.employment.tooltipInfo')}
            />
          )}
        </Stack>
      </Stack>
    </LoadingWrapper>
  )
}

export default EmploymentDescription
